import React, { useContext } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { createAppError } from 'utils/helpers';
import Text from 'components/Text/Text';
import { useTranslation } from 'react-i18next';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import style from './UploaderAndDownloader.module.scss';

interface IPDFUploader {
	onFileLoad: (pdfFile: File) => void;
	onSizeError: () => void;
	fileName: Nullable<string>;
	id: string;
	label: string;
	maxFileSizeMB: number;
	width?: number;
}

const validFileExtensions = ['application/pdf'];

const PDFUploader: React.FC<IPDFUploader> = ({
	onFileLoad,
	onSizeError,
	fileName,
	id,
	label,
	maxFileSizeMB,
	width = 350,
}) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const { t } = useTranslation('error');

	const handleChange = (el: React.ChangeEvent<HTMLInputElement>) => {
		el.preventDefault();
		const pdfFile = el.target.files ? el.target.files[0] : '';
		if (!pdfFile) {
			return;
		} else if (maxFileSizeMB && pdfFile.size > 1000 * 1000 * maxFileSizeMB) {
			// add error handler later
			onSizeError();
		} else if (validFileExtensions.includes(pdfFile.type)) {
			onFileLoad(pdfFile);
		} else {
			const appError = createAppError({ message: t('errorAddingFile'), name: '', stack: '' });
			addAppError(appError);
		}
		el.target.value = '';
	};

	return (
		<div className={style.container} data-test="PDFUploader">
			<input className={style.input} onChange={handleChange} id={id} type="file" accept=".pdf" />
			<label htmlFor={id} style={{ width: `${width}px` }}>
				<div className={style.loader}>
					<div className={style.label}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#uploadArrow`} />
						</svg>
						<Text translationNameSpace="form" translationKey={label} asFragment={true} />
					</div>
				</div>
			</label>
			{fileName && <div className={style.fileName}>{fileName}</div>}
		</div>
	);
};

export default PDFUploader;
